import type { ReactNode } from "react";

/**
 * 라우트 접근 정책.
 * - public: 누구나 접근 가능 (인증 무관)
 * - guestOnly: 비로그인 전용 (로그인 시 차단)
 * - authenticated: 로그인 필요
 * - certified: 본인인증까지 완료된 회원 전용
 * - plannerOnly: 셀프 플래너 회원 전용
 *
 * NOTE: certified/plannerOnly 는 아직 authenticated 와 동일(AuthGuard TODO).
 */
export type RouteAccess =
  | "public"
  | "guestOnly"
  | "authenticated"
  | "certified"
  | "plannerOnly";

/**
 * 기존 보일러플레이트의 `"login" | "notLogin" | null` 표기를 유지하기 위한 별칭.
 * 새로 추가하는 라우트는 `access` 필드를 사용하는 것을 권장한다.
 */
export type LegacyRouteType = "login" | "notLogin" | null;

export type RouteConfig = {
  path: string;
  element: ReactNode;
  /** 신규 권한 표기. 지정하지 않으면 `type` 을 기반으로 변환된다. */
  access?: RouteAccess;
  /** 레거시 호환용. 새 코드에서는 `access` 사용 권장. */
  type?: LegacyRouteType;
  /**
   * 전환 방식 override. 지정하지 않으면 nav-type 기반 기본값(PUSH=전진 슬라이드,
   * POP=후퇴 슬라이드, REPLACE=fade)을 따른다. fade/none 이 필요한 예외 라우트에만 지정.
   * (slide-left=전진(push) 슬라이드, slide-right=후퇴(pop) 슬라이드로 매핑된다.)
   */
  transition?: TransitionType;
};

export type TransitionType = "fade" | "slide-left" | "slide-right" | "none";

/** 라우트 전환 모션(카드 push/pop). mode·dir 결정 규칙과 data-mode/data-nav 전달 이유는 transitions.ts·Router.tsx, 카드 위치·딤 규칙은 _card-motion.scss. */
export type RouteMotion = {
  mode: "fade" | "slide" | "none";
  dir: "push" | "pop";
};

export const resolveAccess = (route: RouteConfig): RouteAccess => {
  if (route.access) return route.access;
  if (route.type === "login") return "authenticated";
  if (route.type === "notLogin") return "guestOnly";
  return "public";
};
